/**
 * Minimal, XSS-safe markdown renderer for coaching messages.
 * Identical across all DDD apps — kept as a local copy to avoid cross-app deps.
 *
 * Australian English throughout.
 */

import React from 'react';

function formatInline(text, keyPrefix) {
  if (!text) return null;
  const nodes = [];
  let i = 0;
  let buf = '';
  const push = (node) => { if (buf) { nodes.push(buf); buf = ''; } nodes.push(node); };

  while (i < text.length) {
    const rest = text.slice(i);

    let m = rest.match(/^\*\*([^*]+)\*\*/);
    if (m) { push(<strong key={`${keyPrefix}-b-${i}`}>{m[1]}</strong>); i += m[0].length; continue; }

    m = rest.match(/^\*([^*\n]+)\*/) || rest.match(/^_([^_\n]+)_/);
    if (m) { push(<em key={`${keyPrefix}-i-${i}`}>{m[1]}</em>); i += m[0].length; continue; }

    m = rest.match(/^`([^`\n]+)`/);
    if (m) {
      push(<code key={`${keyPrefix}-c-${i}`} className="px-1 py-0.5 rounded bg-slate-100 text-slate-800 text-[0.95em]">{m[1]}</code>);
      i += m[0].length; continue;
    }

    m = rest.match(/^\[([^\]]+)\]\((https?:\/\/[^)\s]+)\)/);
    if (m) {
      push(
        <a key={`${keyPrefix}-a-${i}`} href={m[2]} target="_blank" rel="noreferrer noopener"
           className="underline text-blue-700 hover:text-blue-900">{m[1]}</a>
      );
      i += m[0].length; continue;
    }

    buf += text[i];
    i += 1;
  }
  if (buf) nodes.push(buf);
  return nodes;
}

export function renderMarkdown(text) {
  if (!text || typeof text !== 'string') return null;

  const blocks = text.replace(/\r\n/g, '\n').split(/\n{2,}/);
  const out = [];

  blocks.forEach((block, bIdx) => {
    const lines = block.split('\n').map(l => l.replace(/\s+$/, ''));
    if (lines.length === 0 || (lines.length === 1 && !lines[0])) return;

    // Segment the block into runs of one kind — bullet, numbered, or plain
    // text. Models often run an intro line and its bullets together in a
    // single block (no blank line between them); the previous every-line
    // gates rendered such blocks as paragraphs with literal asterisks — the
    // recurring "markdown *" defect across the fleet's assistants.
    const kindOf = (l) =>
      /^\s*[-*]\s+/.test(l) ? 'ul'
        : /^\s*\d+[.)]\s+/.test(l) ? 'ol'
          : 'p';

    let runStart = 0;
    for (let idx = 1; idx <= lines.length; idx++) {
      if (idx < lines.length && kindOf(lines[idx]) === kindOf(lines[runStart])) continue;
      const run = lines.slice(runStart, idx);
      const kind = kindOf(run[0]);
      const rKey = `${bIdx}-${runStart}`;

      if (kind === 'ul') {
        out.push(
          <ul key={`b-${rKey}`} className="list-disc pl-5 space-y-1 my-1">
            {run.map((l, li) => (
              <li key={li}>{formatInline(l.replace(/^\s*[-*]\s+/, ''), `b-${rKey}-${li}`)}</li>
            ))}
          </ul>
        );
      } else if (kind === 'ol') {
        out.push(
          <ol key={`b-${rKey}`} className="list-decimal pl-5 space-y-1 my-1">
            {run.map((l, li) => (
              <li key={li}>{formatInline(l.replace(/^\s*\d+[.)]\s+/, ''), `b-${rKey}-${li}`)}</li>
            ))}
          </ol>
        );
      } else {
        // Paragraph — preserve soft line breaks inside
        out.push(
          <p key={`p-${rKey}`} className="my-1 first:mt-0 last:mb-0">
            {run.flatMap((l, li) => {
              const inline = formatInline(l, `p-${rKey}-${li}`);
              return li === 0 ? inline : [<br key={`br-${rKey}-${li}`} />, ...(inline || [])];
            })}
          </p>
        );
      }
      runStart = idx;
    }
  });

  return out;
}
